/* eslint-disable space-unary-ops */
/* eslint-disable valid-typeof */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getCreditTable } from "src/api/bi/index";
import { getReceiveDetails } from "src/api/sp/index";
import camelCase from "lodash/camelCase";
import stringToNumber from "src/utils/stringToNumber";
import { resetStore } from "src/store/actions";
import { transferDefaultInvoiceLabelPrefix } from "src/config/constants/genericConstants";

export const fetchAllTransfer = createAsyncThunk(
  "/receive/allTransfer",
  (props: any) =>
    getReceiveDetails(
      props.referenceId,
      props.type,
      props.docId,
      props.storeId,
      props?.sendDate
    )
);
export const fetchCreditTable = createAsyncThunk(
  "receive/credit/table",
  (props: any) => getCreditTable(props)
);

const camelizeKeys: any = (obj: any) => {
  if (Array.isArray(obj)) {
    return obj.map((v) => camelizeKeys(v));
  } else if (obj != null && obj.constructor === Object) {
    return Object.keys(obj).reduce(
      (result, key) => ({
        ...result,
        [camelCase(key)]: camelizeKeys(obj[key])
      }),
      {}
    );
  }
  return obj;
};

const addTotal: any = (inventoryObject: any) => {
  let sum = 0;
  Object.keys(inventoryObject).forEach((key) => {
    if (key !== "valuation") {
      sum = sum + inventoryObject[key];
    }
  });
  const totalValue = sum.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  });
  return totalValue;
};

const inventoryAdjustment: any = (inventoryObject: any) => {
  inventoryObject.invoiceType = inventoryObject.adjustmentType;
  inventoryObject.invoiceLabel =
    inventoryObject?.adjustmentType === "Inventory Adjustment"
      ? "Pending Inventory Credits - (DMR)"
      : "Physical Inventory";
  inventoryObject.pendingCredits.departments.total = addTotal(
    inventoryObject.pendingCredits?.departments
  );
  inventoryObject.pendingCredits.departments.valuation =
    inventoryObject.pendingCredits.valuation;
  if (inventoryObject?.deniedCredits) {
    inventoryObject.deniedCredits.departments.total = addTotal(
      inventoryObject.deniedCredits?.departments
    );
    inventoryObject.deniedCredits.departments.valuation =
      inventoryObject.deniedCredits.valuation;
  }
  inventoryObject.showInfo =
    inventoryObject.previouslyApproved &&
    inventoryObject?.status === "Approved";
  if (inventoryObject.showInfo) {
    inventoryObject.showText = "receivingPage.physicalInventoryInfo";
  }
  return inventoryObject;
};

const formatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const initialState: any = {
  data: {},
  table: [],
  status: "idle",
  error: null,
  docId: "",
  total: 0
};

const receiveTypeSlice = createSlice({
  name: "receiveType",
  initialState,
  reducers: {
    reset() {
      return initialState;
    }
  },
  extraReducers(builder) {
    builder
      .addCase(resetStore, (state, action) => {
        return initialState;
      })
      .addCase(fetchAllTransfer.pending, (state) => {
        state.status = "pending";
      })
      .addCase(fetchAllTransfer.fulfilled, (state, { payload, meta }) => {
        try {
          state.status = "idle";
          state.data = camelizeKeys(payload.data);
          state.docId = state.data?.docId;

          const { type } = meta.arg;
          let currentType = "";
          if (state.data?.invoiceType) {
            currentType = camelCase(state.data?.invoiceType);
            transferDataMapper?.[currentType](state, type, payload);
          } else if (
            payload.data?.transactionType === "Yellow" ||
            payload.data?.transactionType === "Red"
          ) {
            currentType = camelCase(payload.data?.transactionType);
            transferDataMapper?.[currentType](state, payload);
          } else if (type === "Price Change" || type === "Markdown") {
            currentType = camelCase(type);
            transferDataMapper?.[currentType](state, type);
          } else if (state.data?.creditType) {
            currentType = camelCase(state.data?.creditType);
            transferDataMapper?.[currentType](state, type);
          } else {
            currentType = camelCase(state?.data?.adjustmentType);
            transferDataMapper?.[currentType](state);
          }
        } catch (e) {
          console.error(e);
          state.status = "error";
        }
      })
      .addCase(fetchAllTransfer.rejected, (state, _action) => {
        state.status = "error";
        state.data = {};
      })
      .addCase(fetchCreditTable.pending, (state) => {
        state.status = "pending";
      })
      .addCase(fetchCreditTable.fulfilled, (state, { payload }) => {
        state.status = "idle";
        state.docId = payload.data.docId;
        state.table = payload.data;
        state.total = state.table.length;
      })
      .addCase(fetchCreditTable.rejected, (state, _action) => {
        state.status = "error";
        state.data = {};
      });
  }
});

const transferDataMapper: { [key: string]: Function } = {
  markdown: (state: any, type: string) => getMarkdownData(state, type),
  priceChange: (state: any, type: string) => getPriceChangeData(state, type),
  inventoryAdjustment: (state: any) => getAdjustmentData(state),
  physicalInventory: (state: any) => getAdjustmentData(state),
  credit: (state: any, type: string, payload: any) =>
    getCreditData(state, type, payload),
  manualTransfer: (state: any, type: string, payload: any) =>
    getManualTransferData(state, type, payload),
  transfer: (state: any, type: string, payload: any) =>
    getTransferData(state, type, payload),
  overShort: (state: any, type: string, payload: any) =>
    getOverShortData(state, type, payload),
  freightClaim: (state: any, type: string, payload: any) =>
    getFreightClaimData(state, type, payload),
  dsd: (state: any, type: string, payload: any) =>
    getDsdData(state, type, payload),
  red: (state: any, payload: any) => getRedData(state, payload),
  yellow: (state: any, payload: any) => getYellowData(state, payload),
  throwaway: (state: any) => getThrowawayData(state),
  pink: (state: any) => getPinkData(state)
};

const getPinkData = (state: any) => getCreditTypeData(state);

const getThrowawayData = (state: any) => getCreditTypeData(state);

const getYellowData = (state: any, payload: any) =>
  getTransactionData(state, payload);

const getRedData = (state: any, payload: any) =>
  getTransactionData(state, payload);

const getFreightClaimData = (state: any, type: string, payload: any) =>
  getDataForInvoicedTypes(state, type, payload);

const getOverShortData = (state: any, type: string, payload: any) =>
  getDataForInvoicedTypes(state, type, payload);

const getTransferData = (state: any, type: string, payload: any) =>
  getDataForInvoicedTypes(state, type, payload);

const getDsdData = (state: any, type: string, payload: any) =>
  getDataForInvoicedTypes(state, type, payload);

const getCreditData = (state: any, type: string, payload: any) =>
  getDataForInvoicedTypes(state, type, payload);

const getManualTransferData = (state: any, type: string, payload: any) =>
  getDataForInvoicedTypes(state, type, payload);

const getMarkdownData = (state: any, type: string) =>
  getDataBasedOnType(state, type);

const getPriceChangeData = (state: any, type: string) =>
  getDataBasedOnType(state, type);

const getCreditTypeData = (state: any) => {
  // For Pink, Throwaway; invoiceType = state.data.creditType
  const stateValue = state.data;
  stateValue.invoiceType = state.data.creditType;
  stateValue.invoiceLabel = state.data.creditType;
  state.data = stateValue;
  if (state.data.creditType === "Pink") {
    const pinkTable: any = [];
    pinkTable.push(state.data.item);
    state.table = pinkTable;
    state.total = pinkTable.length;
  } else {
    state.table = state.data.item;
    state.total = state.data.item.length;
    const { totalAdjustment, totalTransferCost } = state.data.item.reduce(
      (acc: { totalAdjustment: 0; totalTransferCost: 0 }, row: any) => {
        acc.totalAdjustment += stringToNumber(row?.retail || "0");
        acc.totalTransferCost += stringToNumber(row?.transferCost || "0");
        return acc;
      },
      { totalAdjustment: 0, totalTransferCost: 0 }
    );
    state.data.margin =
      ((totalAdjustment - totalTransferCost) / totalAdjustment) * 100;
  }
};

const getTransactionData = (state: any, payload: any) => {
  // For Yellow, Red; invoiceType = payload.data.transactionType
  state.data = payload.data;
  state.data.invoiceType = payload.data.transactionType;
  state.data.invoiceLabel = state.data.transactionType;
  state.table = state.data.item.map((poItem: any) => ({
    ...poItem,
    itemId: poItem.itemNumber,
    expiry: poItem?.expiry ? poItem?.expiry : null,
    estimatedOnHand: poItem?.estimatedOnHand?.toString(),
    approvedOnHand: poItem?.approvedOnHand?.toString()
  }));
};

const getDataForInvoicedTypes = (state: any, type: string, payload: any) => {
  // For DSD, Credit, OverShort, Freight Claim, Transfer, Manual Transfer; invoiceType = payload.data.invoiceType
  state.table = state.data.poItems?.map((item: any) => ({
    ...item,
    storeId: state.data.storeId
  }));
  state.docId = state.data.docId;
  state.total = state?.table?.length ?? 0;

  // If invoiceLabel is null or empty, default to Grocery Outlet WHSE #{wareHouse} for Transfers
  state.data.invoiceLabel = payload.data.invoiceLabel
    ? payload.data.invoiceLabel
    : type === "Transfer"
      ? `${transferDefaultInvoiceLabelPrefix} #${payload.data.wareHouse}`
      : type;
};

const getAdjustmentData = (state: any) => {
  // For Pending Inventory Credits && Physical Inventory, invoiceType = adjustmentType
  state.data = inventoryAdjustment(state.data);
  const tableDataObject: any = [];
  tableDataObject.push({
    ...state.data.pendingCredits.departments,
    departmentPreviousAmounts:
      state.data.pendingCredits?.departmentPreviousAmounts || {}
  });
  if (state?.data?.deniedCredits) {
    tableDataObject.push({
      ...state.data.deniedCredits.departments,
      departmentPreviousAmounts:
        state.data.deniedCredits?.departmentPreviousAmounts || {}
    });
  }
  state.table = tableDataObject;
  state.docId = state.data.docId;
  state.total = tableDataObject.length;
};

const getDataBasedOnType = (state: any, type: string) => {
  // For Markdown and Price Change, invoiceType = meta.arg.type
  state.data.invoiceType = type;
  state.data.invoiceLabel = type;

  const items = state.data.items?.map((item: any) => {
    let adjustment =
      ((+item.newRetail || 0) - (+item.oldRetail || 0)) * (+item.quantity || 0);

    if (Math.abs(adjustment) === 0) {
      adjustment = 0;
    }

    return {
      ...item,
      adjustment: formatter.format(adjustment),
      oldRetail: formatter.format(item.oldRetail),
      newRetail: formatter.format(item.newRetail)
    };
  });

  const totalAdjustedRetail = items?.reduce(
    (total: number, item: any) => total + stringToNumber(item.adjustment),
    0
  );
  state.data.totalAdjustedRetail = formatter.format(totalAdjustedRetail || 0);
  state.table = items;
};

export const { reset } = receiveTypeSlice.actions;

export const selectReceivingTypeApiStatus = (state: any) =>
  state.receiveTypes.status;

export default receiveTypeSlice.reducer;
